import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PhotosInputDS, Spacer, TextCapitalized } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { Badge } from "@qivia/ui/src/designSystem/components/Tag/Badge";
import { TextAreaDS } from "@qivia/ui/src/designSystem/components/Inputs/TextAreaInput";
import "../../../i18n/i18n";

interface RestitutionSummaryContentProps {
  mileage: number;
  isCabinCleaned: boolean;
  isTrunkCleaned: boolean;
  observations: string;
  oldPictures: { uuid: string; url: string }[];
  newPictures: File[];
}

export const RestitutionSummaryContent = (
  props: RestitutionSummaryContentProps,
) => {
  const { t } = useTranslation();

  const stateBadge = (value: boolean) => (
    <Badge
      text={t(`restitution.summary.${value}`)}
      backgroundColor={
        value
          ? colors["colors/system/success/success_ultraLight"]
          : colors["colors/system/error/error_ultraLight"]
      }
      textColor={
        value
          ? colors["colors/system/success/success_normal"]
          : colors["colors/system/error/error_normal"]
      }
    />
  );

  return (
    <>
      <StyledRow>
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.checkDeclared.mileage.label")}
          </TextCapitalized>
        </StyledLabel>
        <TextCapitalized>
          {`${props.mileage} ${t("restitution.checkDeclared.mileage.unit")}`}
        </TextCapitalized>
      </StyledRow>
      <Spacer y={1} />
      <StyledRow>
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.vehicleCondition.isCabinCleaned")}
          </TextCapitalized>
        </StyledLabel>
        {stateBadge(props.isCabinCleaned)}
      </StyledRow>
      <Spacer y={1} />
      <StyledRow>
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.vehicleCondition.isTrunkCleaned")}
          </TextCapitalized>
        </StyledLabel>
        {stateBadge(props.isTrunkCleaned)}
      </StyledRow>
      <Spacer y={1} />
      <StyledLabel>
        <TextCapitalized>
          {t("restitution.vehicleCondition.observations.label")}
        </TextCapitalized>
      </StyledLabel>
      <Spacer y={0.5} />
      <StyledTextArea>
        <TextAreaDS
          value={props.observations}
          $update={() => undefined}
          isDisabled
        />
      </StyledTextArea>
      <Spacer y={1} />
      <StyledLabel>
        <TextCapitalized>
          {t("restitution.checkDeclared.photos.label")}
        </TextCapitalized>
      </StyledLabel>
      <Spacer y={0.5} />
      {props.oldPictures.length > 0 || props.newPictures.length > 0 ? (
        <PhotosInputDS
          newPictures={{ photos: props.newPictures }}
          oldPictures={{ photos: props.oldPictures }}
          withViewIcon
        />
      ) : (
        <StyledEmptyPhotos>
          <TextCapitalized>
            {t("restitution.checkDeclared.photos.empty")}
          </TextCapitalized>
        </StyledEmptyPhotos>
      )}
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledLabel = styled.div`
  ${typographies["Body/L"]}
`;

const StyledEmptyPhotos = styled.div`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledTextArea = styled.div`
  width: 100%;
  & textarea {
    box-sizing: border-box;
  }
`;
